import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/context/i18n";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BANK } from "@/lib/bank";
import { Lock, ShieldCheck, User, Landmark } from "lucide-react";

const input = "h-11 rounded-lg border-[#E2E8F0] focus:ring-1 focus:ring-[#0B2038]";

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, formatPrice, symbol } = useApp();
  const { t } = useI18n();
  const stored = (() => { try { return JSON.parse(sessionStorage.getItem("checkoutItem") || "null"); } catch { return null; } })();
  const item = location.state?.item || stored;
  React.useEffect(() => { if (location.state?.item) sessionStorage.setItem("checkoutItem", JSON.stringify(location.state.item)); }, [location.state]);
  const [form, setForm] = useState({ name: user?.name?.split(" ")[0] || "", surname: user?.name?.split(" ").slice(1).join(" ") || "", email: user?.email || "", phone: "" });
  const [card, setCard] = useState({ number: "", exp: "", cvc: "" });
  const [payMethod, setPayMethod] = useState("card");
  const [submitting, setSubmitting] = useState(false);

  if (!item) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]"><Header />
        <div className="pt-40 text-center"><p className="text-[#475569]">{t("nothing_checkout")}</p>
          <Button onClick={() => navigate("/")} className="mt-4 rounded-full bg-[#0B2038]">{t("back_home")}</Button></div>
      </div>
    );
  }

  const login = () => {
    sessionStorage.setItem("postLoginRedirect", "/checkout");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async () => {
    if (!form.name || !form.surname || !form.email) { toast.error("Please complete guest details"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        productType: item.productType, source: item.source || "hbx",
        title: item.title, subtitle: item.subtitle, image: item.image,
        priceEur: item.priceEur, rateKey: item.rateKey, meta: item.meta || {},
        paymentMethod: payMethod,
        holder: form, travelers: [{ name: form.name, surname: form.surname }],
      });
      toast.success(payMethod === "wire" ? t("wire_placed") : "Booking confirmed!");
      sessionStorage.removeItem("checkoutItem");
      navigate(`/confirmation/${res.data.reference}`, { state: { booking: res.data } });
    } catch (e) { toast.error(e.response?.data?.detail || "Booking failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="pt-24 max-w-5xl mx-auto px-4 lg:px-8">
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-[#0B2038] mb-6">{t("complete_booking")}</h1>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {!user && (
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 flex items-center justify-between gap-3" data-testid="checkout-login-prompt">
                <div><p className="font-semibold text-[#0B2038]">{t("sign_to_confirm")}</p><p className="text-sm text-[#475569]">{t("need_account")}</p></div>
                <Button data-testid="checkout-login-button" onClick={login} className="rounded-full bg-[#0B2038] text-white shrink-0"><User className="w-4 h-4 mr-2" />{t("signin")}</Button>
              </div>
            )}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
              <p className="label-caps mb-4">{t("lead_guest")}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-xs">{t("first_name")}</Label><Input data-testid="checkout-name" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label className="text-xs">{t("last_name")}</Label><Input data-testid="checkout-surname" className={input} value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} /></div>
                <div><Label className="text-xs">{t("email")}</Label><Input data-testid="checkout-email" type="email" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label className="text-xs">{t("phone")}</Label><Input data-testid="checkout-phone" className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
              <p className="label-caps mb-4">{t("pay_method")}</p>
              <div className="grid grid-cols-2 gap-3 mb-5">
                {[["card", t("pay_card")], ["wire", t("pay_wire")]].map(([m, label]) => (
                  <button key={m} data-testid={`pay-method-${m}`} onClick={() => setPayMethod(m)}
                    className={`h-12 rounded-xl border text-sm font-bold transition-colors ${payMethod === m ? "bg-[#0B2038] text-white border-[#0B2038]" : "border-[#E2E8F0] text-[#475569] hover:border-[#0B2038]"}`}>
                    {label}
                  </button>
                ))}
              </div>
              {payMethod === "card" ? (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="card-fields">
                  <div className="sm:col-span-2 flex items-center justify-end -mt-1"><span className="text-xs text-[#475569] flex items-center gap-1"><Lock className="w-3 h-3" />{t("no_charge")}</span></div>
                  <div className="sm:col-span-2"><Label className="text-xs">{t("card_number")}</Label><Input data-testid="checkout-card" className={input} placeholder="4242 4242 4242 4242" value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} /></div>
                  <div><Label className="text-xs">Expiry</Label><Input className={input} placeholder="MM/YY" value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value })} /></div>
                  <div><Label className="text-xs">CVC</Label><Input className={input} placeholder="123" value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} /></div>
                </div>
              ) : (
                <div data-testid="wire-instructions" className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-4 text-sm space-y-2">
                  <p className="font-semibold text-[#0B2038] flex items-center gap-2"><Landmark className="w-4 h-4 text-[#009E9E]" />{t("wire_title")}</p>
                  <p className="text-[#475569]">{t("wire_desc")}</p>
                  <div className="grid gap-1.5 pt-2 border-t border-[#E2E8F0]">
                    <div className="flex justify-between gap-3"><span className="text-[#475569]">{t("account_name")}</span><span className="font-semibold text-[#0B2038] text-right">{BANK.account}</span></div>
                    <div className="flex justify-between gap-3"><span className="text-[#475569]">{t("bank_name")}</span><span className="font-semibold text-[#0B2038] text-right">{BANK.bank}</span></div>
                    <div className="flex justify-between gap-3"><span className="text-[#475569]">IBAN</span><span className="font-mono font-semibold text-[#0B2038]">{BANK.iban}</span></div>
                    <div className="flex justify-between gap-3"><span className="text-[#475569]">SWIFT</span><span className="font-mono font-semibold text-[#0B2038]">{BANK.swift}</span></div>
                  </div>
                  <p className="text-xs text-amber-700 pt-1">{t("wire_ref_note")}</p>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden sticky top-24" data-testid="checkout-summary">
              {item.image && <div className="h-36"><img src={item.image} alt="" className="w-full h-full object-cover" /></div>}
              <div className="p-5">
                <span className="label-caps">{item.productType}</span>
                <h3 className="font-display text-lg font-bold text-[#0B2038] leading-tight mt-1">{item.title}</h3>
                <p className="text-sm text-[#475569] mt-1">{item.subtitle}</p>
                <div className="flex items-center justify-between mt-5 pt-5 border-t border-[#E2E8F0]">
                  <span className="text-[#475569]">{t("total")} ({symbol})</span>
                  <span className="font-display text-2xl font-bold text-[#009E9E]" data-testid="checkout-total">{formatPrice(item.priceEur)}</span>
                </div>
                <Button data-testid="checkout-confirm-button" disabled={!user || submitting} onClick={submit} className="w-full mt-5 h-12 rounded-full bg-[#009E9E] hover:bg-[#007A7A] text-white font-semibold disabled:opacity-50">
                  {submitting ? t("confirming") : user ? (payMethod === "wire" ? t("place_order") : t("confirm_booking")) : t("sign_to_book")}
                </Button>
                <p className="text-xs text-[#475569] flex items-center gap-1 mt-3 justify-center"><ShieldCheck className="w-3.5 h-3.5 text-[#009E9E]" />{t("secured")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
